/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form/FormSectionCollapsible';
import { Label, Text, TextInput } from '@/shared/ui';
import { FieldError } from './FieldError';
import { labelStyle } from './drawer-styles';
import type { ClientFormData } from './schema';
import { MANUAL_ORIGIN_CHANNELS, getVocabularyOptions } from './schema.options';
import { ClientSuggestField } from './ClientSuggestField';
import { VocabularySelect } from './VocabularySelect';

interface OriginSectionProps {
  form: UseFormReturn<ClientFormData>;
  mode: 'create' | 'edit';
  collapsed: boolean;
  onToggle: () => void;
  agencyId?: string;
}

/**
 * One stored origin fact, shown as text: origin is write-once.
 *
 * @param props - The label and the value.
 * @returns The row, or null when the value is blank.
 */
function ReadOnlyRow({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex flex-col">
      <Label style={labelStyle}>{label}</Label>
      <Text variant="bodySm" component="span" className="text-(--text-primary)">
        {value}
      </Text>
    </div>
  );
}

/**
 * Channel, detail and campaign of a new client, with suggestions.
 *
 * @param props - The form and the agency.
 * @returns The three editable origin fields.
 */
function NewOriginFields({ form, agencyId }: { form: UseFormReturn<ClientFormData>; agencyId?: string }) {
  const { t } = useTranslation('features/clients');
  return (
    <>
      <VocabularySelect
        form={form}
        name="origin.channel"
        label={t('profile.origin.channel')}
        placeholder={t('drawer.origin.channelPlaceholder')}
        options={getVocabularyOptions(MANUAL_ORIGIN_CHANNELS, 'profile.origin.channels', t)}
      />
      <ClientSuggestField
        form={form}
        name="origin.sourceDetail"
        field="source_detail"
        agencyId={agencyId}
        label={t('drawer.origin.sourceDetail')}
        placeholder={t('drawer.origin.sourceDetailPlaceholder')}
        maxLength={120}
      />
      <ClientSuggestField
        form={form}
        name="origin.campaign"
        field="campaign"
        agencyId={agencyId}
        label={t('profile.origin.campaign')}
        placeholder={t('drawer.origin.campaignPlaceholder')}
        maxLength={120}
      />
    </>
  );
}

/**
 * The stored origin of an existing client, read-only.
 *
 * @param props - The form.
 * @returns The rows and a line saying why they cannot change.
 */
function StoredOrigin({ form }: { form: UseFormReturn<ClientFormData> }) {
  const { t } = useTranslation('features/clients');
  const origin = form.getValues('origin');
  const channel = origin?.channel
    ? t(`profile.origin.channels.${origin.channel}`, { defaultValue: origin.channel })
    : t('profile.origin.manual');
  return (
    <>
      <ReadOnlyRow label={t('profile.origin.channel')} value={channel} />
      <ReadOnlyRow label={t('drawer.origin.sourceDetail')} value={origin?.sourceDetail} />
      <ReadOnlyRow label={t('profile.origin.campaign')} value={origin?.campaign} />
      <Text variant="meta" component="span" className="text-(--text-tertiary-soft)">
        {t('drawer.origin.writeOnce')}
      </Text>
    </>
  );
}

/**
 * Where the client came from, and their code in the previous system.
 *
 * Origin is write-once on the backend (a PUT carrying it answers 422), so
 * it is editable only while creating; the previous system's code stays
 * editable, it is the one metadata key an update may carry.
 *
 * @param props - The form, the mode, the collapse state and the agency.
 * @returns The collapsible origin section.
 */
export const OriginSection: React.FC<OriginSectionProps> = ({
  form, mode, collapsed, onToggle, agencyId,
}) => {
  const { t } = useTranslation('features/clients');
  return (
    <FormSection
      title={t('profile.origin.title')}
      variant="quiet"
      divider
      collapsed={collapsed}
      onToggle={onToggle}
    >
      {mode === 'create'
        ? <NewOriginFields form={form} agencyId={agencyId} />
        : <StoredOrigin form={form} />}
      <Controller
        name="legacyCode"
        control={form.control}
        render={({ field, fieldState }) => (
          <div className="flex flex-col">
            <Label style={labelStyle}>{t('profile.origin.legacyCode')}</Label>
            <TextInput {...field} placeholder={t('drawer.origin.legacyCodePlaceholder')} maxLength={60} />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />
    </FormSection>
  );
};
